// Opening and restoring Prism backups, in the browser (System & backup page). The server side is
// netlify/lib/backup.mjs (automatic encrypted backups) and exportAll in netlify/lib/core.mjs (downloaded backups).

// An automatic backup file: "PRISMBAK1" + salt(16) + iv(12) + AES-256-GCM(gzip(JSON)), key from the backup
// passphrase with PBKDF2-SHA256 (210,000 rounds).
export async function openBackupBytes(buf, pass) {
  if (new TextDecoder().decode(buf.slice(0, 9)) !== 'PRISMBAK1') throw new Error('This is not a Prism backup file.');
  const salt = buf.slice(9, 25), iv = buf.slice(25, 37), data = buf.slice(37);
  const base = await crypto.subtle.importKey('raw', new TextEncoder().encode(pass), 'PBKDF2', false, ['deriveKey']);
  const key = await crypto.subtle.deriveKey({ name: 'PBKDF2', salt, iterations: 210000, hash: 'SHA-256' }, base, { name: 'AES-GCM', length: 256 }, false, ['decrypt']);
  let plain;
  try { plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, data); } catch (_) { throw new Error('Wrong backup key, or the file is damaged.'); }
  const text = await new Response(new Blob([plain]).stream().pipeThrough(new DecompressionStream('gzip'))).text();
  return JSON.parse(text);
}

// Puts a backup's records back, table by table in batches the server accepts, then its uploaded files
// (those travel only in the automatic backups). call(action, body) is the server call; progress(n) after each batch.
export const RESTORE_ORDER = ['projects', 'project_members', 'invoices', 'quotes', 'estimates', 'deliveries', 'docs', 'settings', 'files', 'trash'];
// Team accounts come first: any missing (a new, empty database) are recreated switched off until an admin sets a new
// password, and anyone already here under the same email is matched to their backed-up id, so access to restricted
// projects and private schedules comes back with them.
export async function restoreBackup(data, call, progress = () => {}) {
  const { idMap = {}, created: usersCreated = 0 } = data.tables.users?.length ? await call('import.users', { rows: data.tables.users }) : {};
  const mapId = id => idMap[id] || id;
  const remap = {
    project_members: r => ({ ...r, user_id: mapId(r.user_id) }),
    docs: r => ({ ...r, coll: String(r.coll).replace(/(^|\/)users\/([^/]+)/, (m, a, id) => a + 'users/' + mapId(id)) })
  };
  for (const t of RESTORE_ORDER) {
    const rows = (data.tables[t] || []).map(remap[t] || (r => r));
    for (let i = 0; i < rows.length;) {
      let batch = [], size = 0;
      while (i < rows.length && batch.length < 400 && size < 2.5e6) { const s = JSON.stringify(rows[i]).length; if (batch.length && size + s > 2.5e6) break; batch.push(rows[i]); size += s; i++; }
      await call('import.rows', { table: t, rows: batch });
      progress(batch.length);
    }
  }
  // backups from before documents were backed up separately carry them inside
  const meta = Object.fromEntries((data.tables.files || []).map(f => [f.key, f]));
  for (const [key, b64] of Object.entries(data.blobs || {})) {
    const m = meta[key] || {};
    await call('file.restore', { key, data: b64, name: m.name, media_type: m.media_type, project_id: m.project_id });
  }
  // newer ones: the server fetches each document from the backup folder's "prism-files", a batch per request
  const missing = [];
  if (data.filesSeparately) {
    const keys = (data.tables.files || []).map(f => f.key);
    for (let i = 0; i < keys.length;) {
      const r = await call('backup.files.restore', { keys: keys.slice(i, i + 25) });
      if (!r.handled) throw new Error('The server could not restore the uploaded documents.');
      missing.push(...(r.missing || [])); i += r.handled; progress(r.handled);
    }
  }
  return { usersCreated, filesMissing: missing };
}
